import React from "react";
import {
    ApiProvider,
    BasePageParameters,
    LocalizationString,
    PageRoute,
    PageRouteProvider,
    ReactUtility
} from "@reapptor-apps/reapptor-react-common";
import {
    DateInput,
    Icon,
    IconSize,
    NumberWidget,
    PageContainer,
    TextInput
} from "@reapptor-apps/reapptor-react-components";
import AuthorizedPage from "@/models/base/AuthorizedPage";
import WaypointMap from "@/components/WaypointMap/WaypointMap";
import Waypoint from "@/models/server/bout/Waypoint";
import PageDefinitions from "@/providers/PageDefinitions";
import EstimateBookingsRequest from "@/models/server/requests/EstimateBookingsRequest";
import EstimatedBooking from "@/models/server/bout/EstimatedBooking";
import {DateUtility, TimeSpan, Utility} from "@reapptor-apps/reapptor-toolkit";
import ShuttleWaypoint from "@/models/server/shuttle/ShuttleWaypoint";
import EstimatedBookingContainer from "./EstimatedBookingContainer/EstimatedBookingContainer";
import EstimateShuttleBookingsRequest from "@/models/server/requests/EstimateShuttleBookingsRequest";
import EstimateShuttleBookingsResponse from "@/models/server/responses/EstimateShuttleBookingsResponse";
import EstimateBookingsResponse from "@/models/server/requests/EstimateBookingsResponse";
import {TicketType} from "@/models/Enums";
import AppConstants from "@/helpers/AppConstants";
import AppController, {RideBookingRules} from "@/pages/AppController";
import PreBookController from "@/pages/PreBookController";
import ServiceProviderController from "@/pages/ServiceProviderController";
import Localizer from "@/localization/Localizer";

import boutStyles from "../../../bout.module.scss";
import styles from "./BoatSelection.module.scss";
import ShuttleTicketPrice from "@/models/server/shuttle/ShuttleTicketPrice";

export interface IBoatSelectionParameters extends BasePageParameters {
    waypoint?: Waypoint;
}

interface IBoatSelectionState {
    waypoint: Waypoint | null;
    loading: boolean;
    passengers: number;
    adults: number;
    children: number;
    lapChild: number;
    bikesAndTrolleys: number;
    pensioners: number;
    reducedFares: number;
    estimatedBookings: EstimatedBooking[];
    maxAvailableCapacity: number;
    availableDates: Date[] | null;
    now: Date;
    openMonth: Date | null;
    rideBookingRules: RideBookingRules;
}

export default class BoatSelection extends AuthorizedPage<IBoatSelectionParameters, IBoatSelectionState> {
    
    _now: Date = Utility.now(); //SET date for testing

    state: IBoatSelectionState = {
        waypoint: null,
        loading: true,
        passengers: PreBookController.passengers ?? 1,
        adults: PreBookController.adults ?? 1,
        children: PreBookController.children ?? 0,
        lapChild: PreBookController.lapChild ?? 0,
        bikesAndTrolleys: PreBookController.bikesAndTrolleys ?? 0,
        pensioners: PreBookController.pensioners ?? 0,
        reducedFares: PreBookController.reducedFares ?? 0,
        estimatedBookings: [],
        maxAvailableCapacity: 0,
        availableDates: null,
        now: this._now, // Utility.now(),
        openMonth: null,
        rideBookingRules: AppController.getRideBookingRules(AppController.getRideBookingSettings(), this._now),
    };

    private readonly _dateInputRef: React.RefObject<DateInput> = React.createRef();
    private readonly _timeInputRef: React.RefObject<DateInput> = React.createRef();

    private async selectBookingAsync(booking: EstimatedBooking): Promise<void> {
        const route: PageRoute = PageDefinitions.bookingDetails(booking);

        await PageRouteProvider.redirectAsync(route);
    }

    private async setBookingsAsync(response: EstimateBookingsResponse | EstimateShuttleBookingsResponse): Promise<void> {
        
        const estimatedBookings: EstimatedBooking[] = response.items;
        const maxAvailableCapacity: number = (response as EstimateBookingsResponse)?.maxAvailableCapacity ?? 0;
        const availableDates: Date[] | null = (response as EstimateShuttleBookingsResponse)?.availableDates ?? null;
        
        await Utility.wait(50);
        
        // restored browser specific local time, server returns country specific time
        const isRideBooking: boolean = (EstimateBookingsResponse.is(response));
        if (isRideBooking) {
            const length: number = estimatedBookings.length;
            for (let i: number = 0; i < length; i++) {
                estimatedBookings[i].bookingTime = this.bookingTime;
            }
        }

        await this.setState({estimatedBookings, maxAvailableCapacity, availableDates, loading: false});
    }

    private async estimatedBookingsAsync(): Promise<void> {
        if (this.waypoint) {

            const shuttleWaypoint: ShuttleWaypoint | null = ShuttleWaypoint.as(this.waypoint);

            if (shuttleWaypoint) {

                const request = new EstimateShuttleBookingsRequest();
                request.shuttleId = shuttleWaypoint.shuttleId;
                request.sourceServicePointId = shuttleWaypoint.sourceId;
                request.destinationServicePointId = shuttleWaypoint.destinationId;
                request.bookingTime = this.bookingTime;
                request.passengers = this.passengers;
                request.adults = (this.askForAdults) ? this.adults : 0;
                request.children = (this.askForChildren) ? this.childrenCount : 0;
                request.lapChild = (this.askForLapChild) ? this.childrenCount : 0;
                request.bikesAndTrolleys = (this.askForBikesAndTrolleys) ? this.bikesAndTrolleys : 0;
                request.pensioners = (this.askForPensioners) ? this.pensioners : 0;
                request.reducedFare = (this.askForReducedFares) ? this.reducedFares : 0;

                await this.setState({loading: true});

                await ApiProvider.backgroundPostAsync("/api/mobileApp/estimateShuttleBookings", request, this, true, (response: EstimateShuttleBookingsResponse) => this.setBookingsAsync(response));

            } else {

                const request = new EstimateBookingsRequest();
                request.waypointId = this.waypoint.id;
                request.bookingTime = this.bookingTime;
                request.passengers = this.passengers;

                await this.setState({loading: true});

                await ApiProvider.backgroundPostAsync("/api/mobileApp/estimateBookings", request, this, true, (response: EstimateBookingsResponse) => this.setBookingsAsync(response));
            }
        }
    }

    private countPassengers(): void {
        const passengers: number = this.adults + this.childrenCount + this.bikesAndTrolleys + this.pensioners + this.lapChild + this.reducedFares;

        this.state.passengers = passengers;

        PreBookController.setPassengers(passengers);
    }

    private async setPassengersAsync(passengers: number): Promise<void> {
        if (this.passengers != passengers) {
            this.state.passengers = passengers;
            this.state.adults = passengers;
            this.state.children = 0;
            this.state.bikesAndTrolleys = 0;
            this.state.lapChild = 0;

            PreBookController.setPassengers(passengers);
            PreBookController.setAdults(passengers);
            PreBookController.setChildren(0);
            PreBookController.setBikesAndTrolleys(0);

            await this.estimatedBookingsAsync();
        }
    }

    private async setAdultsAsync(adults: number): Promise<void> {
        if (this.adults != adults) {

            this.state.adults = adults;

            this.countPassengers();

            PreBookController.setAdults(adults);

            await this.estimatedBookingsAsync();
        }
    }

    private async setPensionersAsync(pensioners: number): Promise<void> {
        if (this.pensioners != pensioners) {

            this.state.pensioners = pensioners;

            this.countPassengers();

            PreBookController.setPensioners(pensioners);

            await this.estimatedBookingsAsync();
        }
    }

    private async setReducedFaresAsync(reducedFares: number): Promise<void> {
        if (this.reducedFares != reducedFares) {

            this.state.reducedFares = reducedFares;

            this.countPassengers();

            PreBookController.setReducedFares(reducedFares);

            await this.estimatedBookingsAsync();
        }
    }

    private async setChildrenAsync(children: number): Promise<void> {
        if (this.childrenCount != children) {
            this.state.children = children;

            this.countPassengers();

            PreBookController.setChildren(children);

            await this.estimatedBookingsAsync();
        }
    }

    private async setBikesAndTrolleysAsync(bikesAndTrolleys: number): Promise<void> {
        if (this.bikesAndTrolleys != bikesAndTrolleys) {
            this.state.bikesAndTrolleys = bikesAndTrolleys;

            this.countPassengers();

            PreBookController.setBikesAndTrolleys(bikesAndTrolleys);

            await this.estimatedBookingsAsync();
        }
    }

    private async setLapChildAsync(lapChild: number): Promise<void> {
        if (this.lapChild != lapChild) {
            this.state.lapChild = lapChild;

            this.countPassengers();

            PreBookController.setLapChild(lapChild);

            await this.estimatedBookingsAsync();
        }
    }

    public get waypoint(): Waypoint | null {
        return this.state.waypoint;
    }

    public get shuttleWaypoint(): ShuttleWaypoint | null {
        return ShuttleWaypoint.as(this.waypoint);
    }

    public get bookingTime(): Date {
        let bookingDate: Date | null = PreBookController.bookingDate;
        
        if (!this.isShuttle) {
            bookingDate = bookingDate ?? new Date(this.now);
            return this.rideBookingRules.getValidBookingTime(bookingDate);
        }
        
        const inPastOrZero: boolean = (bookingDate == null) || (bookingDate.getTime() < this.now.getTime());

        if (inPastOrZero) {
            bookingDate = new Date(this.now);
            bookingDate.setHours(bookingDate.getHours() + 1);
            bookingDate.setMinutes(0);
            bookingDate.setMilliseconds(0);
        }

        return bookingDate!;
    }

    public get isShuttle(): boolean {
        return (this.shuttleWaypoint != null);
    }

    public get passengers(): number {
        return this.state.passengers;
    }

    public get adults(): number {
        return this.state.adults;
    }

    public get childrenCount(): number {
        return this.state.children;
    }

    public get lapChild(): number {
        return this.state.lapChild;
    }

    public get bikesAndTrolleys(): number {
        return this.state.bikesAndTrolleys;
    }

    public get pensioners(): number {
        return this.state.pensioners;
    }

    public get reducedFares(): number {
        return this.state.reducedFares;
    }

    public get estimatedBookings(): EstimatedBooking[] {
        return this.state.estimatedBookings;
    }

    public get maxAvailableCapacity(): number {
        return this.state.maxAvailableCapacity;
    }
    
    private getMaxPassengers(ticketType: TicketType | null = null): number {
        if (this.shuttleWaypoint != null) {
            
            const ticketPrice: ShuttleTicketPrice | null = this.shuttleWaypoint.ticketPrices?.firstOrDefault(item => item.type == ticketType) ?? null;
            
            return ticketPrice?.maxTickets || this.shuttleWaypoint.maxTickets || ServiceProviderController.shuttleMaxTickets;
        }
        
        return AppConstants.defaultRideMaxFare;
    }

    public get maxPassengers(): number {
        return (this.shuttleWaypoint != null)
            ? this.shuttleWaypoint.maxTickets || ServiceProviderController.shuttleMaxTickets
            : AppConstants.defaultRideMaxFare;
    }

    public get loading(): boolean {
        return this.state.loading;
    }

    public get notFound(): boolean {
        return (this.waypoint != null) && (!this.loading) && (this.estimatedBookings.length === 0);
    }

    public get singleTicketType(): boolean {
        return (!this.askForAdults && !this.askForChildren && !this.askForBikesAndTrolleys && !this.askForPensioners && !this.askForLapChild);
    }

    public get askForAdults(): boolean {
        return (this.shuttleWaypoint != null) && (this.shuttleWaypoint.ticketTypes.contains(TicketType.Adult));
    }

    public get askForChildren(): boolean {
        return (this.shuttleWaypoint != null) && (this.shuttleWaypoint.ticketTypes.contains(TicketType.Children));
    }

    public get askForLapChild(): boolean {
        return (this.shuttleWaypoint != null) && (this.shuttleWaypoint.ticketTypes.contains(TicketType.LapChild));
    }

    public get askForBikesAndTrolleys(): boolean {
        return (this.shuttleWaypoint != null) && (this.shuttleWaypoint.ticketTypes.contains(TicketType.BikesAndTrolleys));
    }

    public get askForPensioners(): boolean {
        return (this.shuttleWaypoint != null) && (this.shuttleWaypoint.ticketTypes.contains(TicketType.Pensioners));
    }

    public get askForReducedFares(): boolean {
        return (this.shuttleWaypoint != null) && (this.shuttleWaypoint.ticketTypes.contains(TicketType.ReducedFare));
    }

    public get adultsMin(): number {
        const otherAdultSelected: boolean = (
            ((this.askForPensioners) && (this.pensioners > 0)) ||
            ((this.askForReducedFares) && (this.reducedFares > 0))
        );
        return (!otherAdultSelected)
            ? 1
            : 0;
    }

    public get pensionersMin(): number {
        const otherAdultSelected: boolean = (
            ((this.askForAdults) && (this.adults > 0)) ||
            ((this.askForReducedFares) && (this.reducedFares > 0))
        );
        return (!otherAdultSelected)
            ? 1
            : 0;
    }

    public get reducedFaresMin(): number {
        const otherAdultSelected: boolean = (
            ((this.askForAdults) && (this.adults > 0)) ||
            ((this.askForPensioners) && (this.pensioners > 0))
        );
        return (!otherAdultSelected)
            ? 1
            : 0;
    }

    public getTitle(): string {
        return Localizer.mobileBoatSelectionPageTitle;
    }

    public hasSpinner(): boolean {
        return true;
    }

    private async onChangeBookingTimeAsync(): Promise<void> {
        await this.estimatedBookingsAsync();
    }

    private async openDateAsync(): Promise<void> {
        await this._dateInputRef.current?.openAsync();
    }

    private async openTimeAsync(): Promise<void> {
        await this._timeInputRef.current?.openAsync();
    }

    private async onOpenCalendarAsync(sender: DateInput): Promise<void> {
        this.state.openMonth = sender.value.firstDayOfTheMonth();
        this.assignDatesClassNames();
    }
    
    private async onMonthChangeAsync(value: Date): Promise<void> {
        this.state.openMonth = value.firstDayOfTheMonth();
        this.assignDatesClassNames();
    }

    private async setDateAsync(value: Date): Promise<void> {
        
        const date: Date = value.date();
        const time: TimeSpan = DateUtility.time(this.bookingTime);

        value = DateUtility.add(date, time);

        PreBookController.setBookingDate(value);

        await this.reRenderAsync();

        await this.onChangeBookingTimeAsync()
    }

    private async setTimeAsync(value: Date): Promise<void> {
        
        const date: Date = this.bookingTime.date();
        const time: TimeSpan = DateUtility.time(value);
        value = DateUtility.add(date, time);

        PreBookController.setBookingDate(value);

        await this.reRenderAsync();

        await this.onChangeBookingTimeAsync()
    }

    private get now(): Date {
        return this.state.now;
    }

    private get today(): Date {
        return this.now.date();
    }

    private get rideBookingRules(): RideBookingRules {
        return this.state.rideBookingRules;
    }

    public get outOfService(): boolean {
        return (this.serviceProviderSlug?.outOfService == true);
    }

    public get outOfServiceMessage(): string {
        return LocalizationString.value(this.serviceProviderSlug?.outOfServiceMessage);
    }

    public get isBookingAvailable(): boolean {
        return ((this.isShuttle) || (this.rideBookingRules.isBookingAvailable)) && (!this.outOfService);
    }

    public get noBookingAvailableMessage(): string {
        return this.rideBookingRules.noBookingAvailableMessage;
    }
    
    public get boatSelectionHeader(): string {
        return (this.isShuttle)
            ? Localizer.mobileBoatSelectionPageChooseShuttleSpan
            : Localizer.mobileBoatSelectionPageChooseBoatSpan;
    }
    
    public get availableDates(): Date[] | null {
        return this.state.availableDates;
    }

    public get minDate(): Date {
        return (!this.isShuttle)
            ? this.rideBookingRules.getMinDate()
            : this.today;
    }

    private get minTime(): Date {
        const startOfTheDay: Date = this.today;
        startOfTheDay.setHours(0, 0, 0, 0);

        return (!this.isShuttle)
            ? this.rideBookingRules.getMinTime(this.bookingTime)
            : (this.bookingTime.isToday())
                ? this.now
                : startOfTheDay;
    }

    public get maxDate(): Date | null {
        return (!this.isShuttle)
            ? this.rideBookingRules.getMaxDate()
            : null;
    }

    private get maxTime(): Date | null {
        const endOfTheDay: Date = this.today;
        endOfTheDay.setHours(23, 59, 59, 999);

        return (!this.isShuttle)
            ? this.rideBookingRules.getMaxTime(this.bookingTime)
            : endOfTheDay;
    }

    private assignDatesClassNames(): void {
        const dateInput: DateInput | null = this._dateInputRef.current;
        const openMonth: Date | null = this.state.openMonth;
        if ((dateInput) && (this.availableDates) && (openMonth)) {

            const availableDates: Date[] = this.availableDates;

            const node = this.JQuery("#" + dateInput.id);
            const children = node.find(".react-datepicker__week div.react-datepicker__day");
            
            const datesNodes: JQuery[] = [];
            for (let i: number = 0; i < children.length; i++) {
                datesNodes.push(this.JQuery(children[i]));
            }

            const firstIndex: number = datesNodes.findIndex(date => !date.hasClass("react-datepicker__day--outside-month"));
            
            //let date: Date = new Date(dateInput.value);
            //date.setDate(1);
            //date = date.date();
            let date: Date = openMonth;
            
            if (firstIndex > 0) {
                date = date.addDays(-firstIndex);
            }

            for (let i: number = 0; i < datesNodes.length; i++) {
                const dateNode: JQuery = this.JQuery(children[i]);
                const inPast: boolean = date.inPast(true);
                const isAvailableDate: boolean = availableDates.any(item => item.getTime() == date.getTime());
                
                const dateClassNames: string[] = (inPast)
                    ? [styles.calendarDate]
                    : (isAvailableDate)
                        ? [styles.calendarDate, styles.available]
                        : [styles.calendarDate, styles.notAvailable];

                // remove obsolete classes
                const classNamesToRemove: string[] = [styles.notAvailableDate];
                classNamesToRemove.remove(dateClassNames);

                for (let j: number = 0; j < classNamesToRemove.length; j++) {
                    const classNameToRemove: string = classNamesToRemove[j];
                    if (dateNode.hasClass(classNameToRemove)) {
                        dateNode.removeClass(classNameToRemove);
                    }
                }

                // add new class
                for (let j: number = 0; j < dateClassNames.length; j++) {
                    const dateClassName: string = dateClassNames[j];
                    if (!dateNode.hasClass(dateClassName)) {
                        dateNode.addClass(dateClassName);
                    }
                }

                date = date.addDays(1);
            }
        }
    }

    public async initializeAsync(): Promise<void> {
        const waypointId: string | null = this.routeId;

        let waypoint: Waypoint | null = this.parameters?.waypoint ?? null;

        if ((!waypointId) && (!waypoint)) {
            await PageRouteProvider.redirectAsync(PageDefinitions.mobileHomeRoute, true, true);
        }

        waypoint = waypoint ?? await this.postAsync("/api/mobileApp/getWaypoint", waypointId);

        await this.setState({waypoint});

        await this.estimatedBookingsAsync();
    }

    public renderRouteName(): React.ReactNode {
        const routeName: string = "{0}".format(this.waypoint);

        return (
            <div className={styles.routeNameContainer}>

                <span className={styles.routeName}>{routeName}</span>

            </div>
        );
    }

    public renderDateSelection(isBookingAvailable: boolean): React.ReactNode {
        const bookingTime: Date = (isBookingAvailable)
            ? this.bookingTime
            : this.now;
        const date: string = "{0:dddd}, {1:D}".format(bookingTime, bookingTime);
        const time: string = Localizer.mobileDateInfoBookingTime.format(bookingTime);

        return (
            <div className={styles.date}>

                <span className={styles.header}>{Localizer.mobileBoatSelectionPageSelectDateAndTime}</span>
                
                <div className={styles.dateInput}>

                    <DateInput ref={this._dateInputRef}
                               title={Localizer.mobileBoatSelectionPageSelectDateTitle}
                               className={this.css(styles.input)}
                               minDate={this.minDate}
                               maxDate={this.maxDate}
                               value={this.bookingTime}
                               readonly={!isBookingAvailable}
                               onChange={(value: Date) => this.setDateAsync(value)}
                               onExpand={(sender) => this.onOpenCalendarAsync(sender)}
                               onMonthChange={(_, date) => this.onMonthChangeAsync(date)}
                    />

                    <TextInput readonly
                               className={styles.label}
                               title={Localizer.mobileBoatSelectionPageSelectDateTitle}
                               value={date}
                               onClick={() => this.openDateAsync()}
                    />

                </div>

                <div className={styles.timeInput}>

                    <DateInput showTime showOnlyTime
                               ref={this._timeInputRef}
                               timeIntervals={15}
                               className={styles.input}
                               title={Localizer.mobileBoatSelectionPageSelectTimeTitle}
                               minDate={this.minTime}
                               minTime={this.minTime}
                               maxDate={this.maxTime}
                               maxTime={this.maxTime}
                               value={this.bookingTime}
                               readonly={!isBookingAvailable}
                               onChange={(value: Date) => this.setTimeAsync(value)}
                    />

                    <TextInput readonly
                               className={styles.label}
                               title={Localizer.mobileBoatSelectionPageSelectTimeTitle}
                               value={time}
                               onClick={() => this.openTimeAsync()}
                    />

                </div>

            </div>
        );
    }

    public renderAmountSelection(ticketType: TicketType | null = null, title: string | null = null, min: number, value: number, onChange: (value: number) => Promise<void>): React.ReactNode {

        const maxByTotal: number = (this.maxPassengers - this.passengers + value);
        const maxByType: number = this.getMaxPassengers(ticketType);
        const max: number = Math.min(maxByTotal, maxByType);
        const disabled: boolean = (max == 0);
        
        const selectedStyle: any = (value > 0) && styles.selected;

        return (
            <div className={this.css(styles.amountContainer, selectedStyle)}>

                {
                    (title) &&
                    (
                        <div className={styles.amountTitle}>
                            <span>{ReactUtility.toTags(title)}</span>
                            <Icon name={"fas fa-caret-right"} size={IconSize.Large}/>
                        </div>
                    )
                }

                <NumberWidget className={styles.amount}
                              minusIcon={{name: "fas fa-minus"}}
                              plusIcon={{name: "fas fa-plus"}}
                              min={min}
                              max={max}
                              readonly={disabled}
                              value={value}
                              onChange={(_, value) => onChange(value)}
                />

            </div>
        )
    }

    public renderBoatsSelection(): React.ReactNode {
        return (
            <div className={styles.boat}>

                <span className={styles.header}>{this.boatSelectionHeader}</span>

                {
                    (this.singleTicketType) &&
                    (
                        this.renderAmountSelection(null, null, 1, this.passengers, (value) => this.setPassengersAsync(value))
                    )
                }

                {
                    (this.askForAdults) &&
                    (
                        this.renderAmountSelection(TicketType.Adult, Localizer.mobileBoatSelectionPageAdults, this.adultsMin, this.adults, (value) => this.setAdultsAsync(value))
                    )
                }

                {
                    (this.askForPensioners) &&
                    (
                        this.renderAmountSelection(TicketType.Pensioners, Localizer.mobileBoatSelectionPagePensioners, this.pensionersMin, this.pensioners, (value) => this.setPensionersAsync(value))
                    )
                }

                {
                    (this.askForReducedFares) &&
                    (
                        this.renderAmountSelection(TicketType.ReducedFare, Localizer.mobileBoatSelectionPageReducedFares, this.reducedFaresMin, this.reducedFares, (value) => this.setReducedFaresAsync(value))
                    )
                }

                {
                    (this.askForChildren) &&
                    (
                        this.renderAmountSelection(TicketType.Children, Localizer.mobileBoatSelectionPageChildren, 0, this.childrenCount, (value) => this.setChildrenAsync(value))
                    )
                }

                {
                    (this.askForLapChild) &&
                    (
                        this.renderAmountSelection(TicketType.LapChild, Localizer.mobileBoatSelectionPageLapChild, 0, this.lapChild, (value) => this.setLapChildAsync(value))
                    )
                }

                {
                    (this.askForBikesAndTrolleys) &&
                    (
                        this.renderAmountSelection(TicketType.BikesAndTrolleys, Localizer.mobileBoatSelectionPageBikesAndTrolleys, 0, this.bikesAndTrolleys, (value) => this.setBikesAndTrolleysAsync(value))
                    )
                }

                <div className={styles.list}>

                    <EstimatedBookingContainer bookings={this.estimatedBookings}
                                               maxAvailableCapacity={this.maxAvailableCapacity}
                                               passengers={this.passengers}
                                               bikesAndTrolleys={this.bikesAndTrolleys}
                                               loading={this.loading}
                                               isShuttle={this.isShuttle}
                                               onClick={(_, booking) => this.selectBookingAsync(booking)}
                    />

                </div>

            </div>
        );
    }

    public render(): React.ReactNode {
        const isBookingAvailable: boolean = this.isBookingAvailable;
        const isShuttle: boolean = this.isShuttle;

        return (
            <PageContainer transparent fullHeight
                           fullWidth={this.mobile}
                           className={this.css(boutStyles.pageContainer, styles.boatSelection, this.mobile && styles.mobile)}
                           alertClassName={boutStyles.alert}
            >

                <div className={styles.map}>
                    {
                        (this.waypoint) &&
                        (
                            <WaypointMap readonly
                                         point={this.waypoint}
                            />
                        )
                    }
                </div>

                <div className={styles.selectionContainer}>

                    {
                        (isShuttle) && this.renderRouteName()
                    }

                    {
                        this.renderDateSelection(isBookingAvailable)
                    }

                    {
                        (isBookingAvailable)
                            ?
                            (
                                this.renderBoatsSelection()
                            )
                            :
                            (
                                <span className={styles.noBookingAvailable}>
                                    {
                                        (this.outOfService)
                                            ? ReactUtility.toMultiLines(this.outOfServiceMessage)
                                            : ReactUtility.toMultiLines(this.noBookingAvailableMessage)
                                    }
                                </span>
                            )
                    }

                </div>

            </PageContainer>
        );
    }
}